import { isAccountStatusReadOnly } from 'utils/clickme';
import { CampaignProductType } from 'utils/constants/campaign';

import {
    CAMPAIGNS_TABLE_COLUMNS_FULL,
    CAMPAIGNS_TABLE_COLUMNS_IMPORTANT,
    CampaignsTableColumn,
} from 'components/CampaignsDashboard/components/CampaignsTable/columns';

import {
    CampaignBanner,
    CampaignBannerModerationStatus,
    CampaignFrontendStatus,
    DashboardCampaign,
} from 'store/models/campaignsDashboard';
import { ColumnsPreset } from 'store/models/campaignsFilters';
import { AccountStatus } from 'store/models/user';

const LOW_BALANCE_THRESHOLD = 500;

export const getIsInsufficientBalance = (
    realBalance: number,
    bid: number,
    frontendStatus: CampaignFrontendStatus
): boolean =>
    ![
        CampaignFrontendStatus.Archived,
        CampaignFrontendStatus.Blocked,
        CampaignFrontendStatus.InactiveDueToEndDate,
        CampaignFrontendStatus.ActiveWithDelayedStart,
        CampaignFrontendStatus.Active,
    ].includes(frontendStatus) &&
    (realBalance < bid || realBalance < LOW_BALANCE_THRESHOLD);

export const getUrlToCopyCampaign = (campaignId: number, banners: CampaignBanner[]): string => {
    const bannerId = banners.slice(-1)[0].id;
    return `/new/campaign/${campaignId}/banner/${bannerId}/copy`;
};

export const getIsActiveStatus = (status: CampaignFrontendStatus): boolean => {
    return [
        CampaignFrontendStatus.Active,
        CampaignFrontendStatus.ActiveWithDelayedStart,
        CampaignFrontendStatus.Unknown,
    ].includes(status);
};

export const getIsInactiveStatus = (status: CampaignFrontendStatus): boolean => {
    return [
        CampaignFrontendStatus.InactiveDueToEndDate,
        CampaignFrontendStatus.BudgetExceeded,
        CampaignFrontendStatus.DailyBudgetExceedStatus,
    ].includes(status);
};

export const getIsMutatingActionAvailable = (
    status: CampaignFrontendStatus,
    currentAccountStatus?: AccountStatus
): boolean => {
    return status !== CampaignFrontendStatus.Archived && !isAccountStatusReadOnly(currentAccountStatus);
};

export enum CampaignTableModerationStatus {
    InProgress = 'inProgress',
    Rejected = 'rejected',
    PartialFailed = 'partialFailed',
    None = 'none',
}

export const getCampaignTableModerationStatus = (
    banners: CampaignBanner[],
    productType: CampaignProductType,
    frontendStatus: CampaignFrontendStatus
): CampaignTableModerationStatus => {
    if (
        productType === CampaignProductType.SearchAuction ||
        frontendStatus === CampaignFrontendStatus.Archived ||
        frontendStatus === CampaignFrontendStatus.Blocked
    ) {
        return CampaignTableModerationStatus.None;
    }

    const isSomeBannersModerationNew = banners?.some(
        ({ moderationStatus }) => moderationStatus === CampaignBannerModerationStatus.New
    );
    if (isSomeBannersModerationNew) {
        return CampaignTableModerationStatus.InProgress;
    }

    const isAllBannersModerationBanned = banners?.every(
        ({ moderationStatus }) => moderationStatus === CampaignBannerModerationStatus.Banned
    );
    if (isAllBannersModerationBanned) {
        return CampaignTableModerationStatus.Rejected;
    }

    const isSomeBannersModerationBanned = banners?.some(
        ({ moderationStatus }) => moderationStatus === CampaignBannerModerationStatus.Banned
    );
    if (isSomeBannersModerationBanned) {
        return CampaignTableModerationStatus.PartialFailed;
    }

    return CampaignTableModerationStatus.None;
};

export const convertCampaignsToMap = (campaigns: DashboardCampaign[]): Map<number, DashboardCampaign> =>
    campaigns.reduce((acc, campaign) => {
        acc.set(campaign.id, campaign);
        return acc;
    }, new Map<number, DashboardCampaign>());

export const getUrlToBanners = (campaignId: number): string => {
    return `/new/campaign/${campaignId}/banners`;
};

export const getCampaignsTableColumns = (columnsPreset: ColumnsPreset): CampaignsTableColumn[] => {
    return columnsPreset === ColumnsPreset.Important ? CAMPAIGNS_TABLE_COLUMNS_IMPORTANT : CAMPAIGNS_TABLE_COLUMNS_FULL;
};

export const getIsCampaignNeedShowCheckbox = (
    frontendStatus: CampaignFrontendStatus,
    budget: number | null,
    isMutatingActionAvailable: boolean
): boolean => {
    return !(budget === null && frontendStatus === CampaignFrontendStatus.Blocked) && isMutatingActionAvailable;
};
